import type { ReactNode } from 'react';
import { FluxterMark } from '@/components/FluxterMark';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 7b.
 *
 * Centered-glass-card shell shared by every pre-auth or auth-adjacent
 * surface: Login, Onboarding, AuthCallback, InvitationAccept,
 * GithubInstallCallback. Per Q1 (auth pages = centered card with
 * FluxterMark above) and Q7 (no workspace shell, no topbar).
 *
 * Lays the workspace's gradient backdrop on the full viewport plus
 * the radial brand-glow behind the card so auth surfaces feel
 * continuous with the rest of the app rather than dropping into a
 * plain white modal context.
 *
 * Width tiers:
 *   sm — Login (single CTA, tight)
 *   md — Onboarding / InvitationAccept / GithubInstallCallback (forms,
 *        copy + CTA stacks)
 *   lg — reserved for future signup-with-fields flows
 */

export interface AuthShellProps {
  /** Mark + heading + sub-line slot. Pass `<AuthHeader>` for the
   *  conventional treatment, or any custom node. */
  header: ReactNode;
  /** Form / status / error block under the header. */
  children: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  className?: string;
}

const WIDTH_CLASS: Record<NonNullable<AuthShellProps['width']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export const AuthShell = ({
  header,
  children,
  width = 'md',
  className,
}: AuthShellProps) => (
  <main
    className="relative isolate flex min-h-screen items-center justify-center p-6"
    style={{ background: 'var(--shell-bg)' }}
  >
    {/*
      Same radial backdrop glow used on board / calendar / detail
      surfaces. Aria-hidden, pointer-events-none, blurred 20px so
      the card stays sharp but the page doesn't feel flat.
    */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
      style={{
        background:
          'radial-gradient(60% 50% at 80% 0%, var(--brand-glow-1), transparent 70%),' +
          'radial-gradient(50% 60% at 10% 30%, var(--brand-glow-2), transparent 60%)',
      }}
    />
    <div
      className={cn(
        'relative z-[1] w-full space-y-6 rounded-2xl border p-8 backdrop-blur-md',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
        'shadow-[var(--glass-card-shadow)]',
        WIDTH_CLASS[width],
        className,
      )}
    >
      {header}
      {children}
    </div>
  </main>
);

export interface AuthHeaderProps {
  title: string;
  subtitle?: ReactNode;
}

/**
 * Conventional header for AuthShell — FluxterMark above a 22px
 * title and a faint sub-line. Title scale matches the prototype's
 * `.cal-title` rather than the larger task-detail title; auth cards
 * are bounded so they need a quieter heading.
 */
export const AuthHeader = ({ title, subtitle }: AuthHeaderProps) => (
  <div className="space-y-3 text-center">
    <FluxterMark className="mx-auto h-9 w-9" />
    <div className="space-y-1">
      <h1 className="text-foreground text-[22px] font-semibold tracking-[-0.02em]">
        {title}
      </h1>
      {subtitle ? (
        <p className="text-foreground-faint text-[12.5px]">{subtitle}</p>
      ) : null}
    </div>
  </div>
);
